import { ContentCopy, Phone } from "@mui/icons-material";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";

import { useToast } from "~hooks/useToast";
import { formatOnlyDigits, formatPhoneNumberWithCountryCode, phoneToTelHref } from "~utils/form/format";

const tCopyPhoneNumber = "Copy phone number";
const tCopiedToClipboard = "Phone number copied to clipboard";

export interface PhoneWithCopyIconProps {
  phoneNumber: string;
  /** Show as tel: link. Default true */
  asLink?: boolean;
  /** Show phone icon before number. Default true */
  showPhoneIcon?: boolean;
  /** Center the phone + copy group (e.g. roster cards). */
  centered?: boolean;
}

export const PhoneWithCopyIcon = ({
  phoneNumber,
  asLink = true,
  showPhoneIcon = true,
  centered = false,
}: PhoneWithCopyIconProps) => {
  const toast = useToast();
  const displayValue = formatPhoneNumberWithCountryCode(phoneNumber);
  const digitsOnly = formatOnlyDigits(phoneNumber);
  const telHref = phoneToTelHref(phoneNumber);

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!digitsOnly) {
      return;
    }
    try {
      await navigator.clipboard.writeText(digitsOnly);
      toast.success(tCopiedToClipboard);
    } catch {
      toast.error("Failed to copy phone number");
    }
  };

  const phoneContent = (
    <Box
      component={asLink && telHref ? "a" : "div"}
      href={asLink && telHref ? telHref : undefined}
      sx={{
        alignItems: "center",
        color: "primary.main",
        display: "flex",
        flex: "0 1 auto",
        gap: 0.5,
        justifyContent: centered ? "center" : undefined,
        maxWidth: centered ? "100%" : undefined,
        minWidth: 0,
        textDecoration: asLink ? "none" : undefined,
      }}
    >
      {showPhoneIcon && <Phone fontSize="small" sx={{ color: "inherit", flexShrink: 0 }} />}
      <Typography
        component="span"
        variant="body2"
        sx={{
          minWidth: 0,
          overflowWrap: "break-word",
        }}
      >
        {displayValue}
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        alignItems: "center",
        display: "flex",
        flexWrap: "wrap",
        gap: 0.5,
        justifyContent: centered ? "center" : "flex-start",
        maxWidth: "100%",
        minWidth: 0,
        width: "100%",
      }}
    >
      {phoneContent}
      <Tooltip title={tCopyPhoneNumber}>
        <IconButton aria-label={tCopyPhoneNumber} onClick={handleCopy} size="small" sx={{ flexShrink: 0 }}>
          <ContentCopy fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};
